import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  NumberInput,
  RadioItem,
  Separator,
} from 'platform/components';
import {Box, Grid, GridItem, Hide, HStack, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';
import {Nullish} from 'utility-types';
import {z} from 'zod';

import {RefObject} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {defaultTo, includes, isNil, not, omit, sum} from 'ramda';
import {isFalse, isFalsy, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetOperationItemsQuery} from '@dms/api/finance';
import {
  useGetLabourRatesListQuery,
  useGetMetadaServiceCaseQuery,
  useGetServiceOrderJobItemWorkQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
  usePatchOrderItemForWorkMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  getDecimalFromPercentage,
  getPercentageFromDecimal,
  handleApiError,
  useGetVatRatesOptions,
  usePermissions,
  useTenant,
  useWorkTypeOptions,
} from '@dms/shared';
import {ItemPrice} from '@dms/shared-aftersales';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getRatioFromAmount} from '../../../utils/getRatioFromAmount';
import {editItemFormSchema, EditItemFormType} from '../types/editItemFormSchema';
import {EditItemMechanics} from './EditItemMechanics';

const TOTAL_RATIO = 100;

interface EditItemProps extends TestIdProps {
  itemId: string;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  onClose: VoidFunction;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

export function EditItemForm(props: EditItemProps) {
  const tenant = useTenant();
  const formatPrice = useFormatCurrency();

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});
  const {
    data: itemData,
    isLoading: isItemLoading,
    isFetching: isItemFetching,
    isError: isItemError,
  } = useGetServiceOrderJobItemWorkQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceItemId: props.itemId,
      serviceJobId: props.serviceJobId,
      serviceOrderId: props.serviceOrderId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const {
    options: labourRatesOptions,
    isLoading: isLabourRatesLoading,
    isError: isLabourRatesError,
  } = useGetLabourRatesListQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {
      selectFromResult: (data) => ({
        ...data,
        options: data?.data?.map((labourRate) => ({
          label: formatPrice(
            labourRate.rateValue.withoutVat.amount ?? 0,
            labourRate.rateValue.withoutVat.currency ?? tenant.tenantCurrency
          ),
          value: labourRate.labourRateId,
        })),
      }),
    }
  );

  const {
    options: workItemGroupOptions,
    isLoading: isWorkItemGroupLoading,
    isError: isWorkItemGroupError,
  } = useGetOperationItemsQuery(
    {application: 'workshop', itemGroup: 'work'},
    {
      selectFromResult: (data) => ({
        ...data,
        options:
          data?.data?.map((item) => ({label: item.itemCodeName, value: item.operationId})) ?? [],
      }),
    }
  );
  const {data: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {data: serviceOrderVariant} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: String(serviceOrder?.serviceOrderVariantId ?? '')},
    {skip: !serviceOrder?.serviceOrderVariantId}
  );

  const [patchOrderItemForWork, {isLoading: isPatchLoading}] = usePatchOrderItemForWorkMutation({
    fixedCacheKey: 'edit-service-order-work-item',
  });

  const isOrderReadOnly =
    includes(serviceOrder?.state, ['CLOSED', 'CANCELLED']) ||
    includes(serviceCase?.state, ['CANCELLED']);

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});
  const {getOptionsWithSelectedValue} = useWorkTypeOptions();

  const [canEditCustomPrice, canEditManualDiscount, canReadServiceOrderFinancesLabourItem] =
    usePermissions({
      permissionKeys: [
        'serviceItemLabourCustomPriceEdit',
        'serviceItemLabourManualDiscountEdit',
        'serviceOrderFinancesLabourItemRead',
      ],
      scopes: {
        serviceItemLabourCustomPriceEdit: {
          branchId: serviceCase?.branchId ?? '',
          serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
          serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
        },
        serviceItemLabourManualDiscountEdit: {
          branchId: serviceCase?.branchId ?? '',
          serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
          serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
        },
        serviceOrderFinancesLabourItemRead: {
          branchId: serviceCase?.branchId ?? '',
          serviceCaseType: serviceOrderVariant?.serviceCaseTypeId ?? '',
          serviceOrderType: serviceOrderVariant?.serviceOrderTypeId ?? '',
        },
      },
    });

  const handleSubmit: FormSubmitHandler<EditItemFormType> = async (data, setErrors) => {
    if (
      data.assignMechanics &&
      sum(data.assignMechanics.map((mechanic) => mechanic?.ratio ?? 0)) !== TOTAL_RATIO
    ) {
      return setErrors(
        data.assignMechanics.map((_, index) => ({
          name: `assignMechanics.${index}.ratio`,
          message: i18n.t('entity.order.labels.ratioSumError'),
        }))
      );
    }

    await patchOrderItemForWork({
      serviceCaseId: props.serviceCaseId,
      serviceItemId: props.itemId,
      serviceJobId: props.serviceJobId,
      serviceOrderId: props.serviceOrderId,
      body: {
        name: data.name ?? '',
        number: data.number,
        workType: data.workType ?? '',
        labourRateId: data.labourRateId ?? '',
        quantity: data.quantity ?? 0,
        sellingPricePerUnit: data.sellingPricePerUnit ?? 0,
        purchasePricePerUnit: data.purchasePricePerUnit ?? 0,
        isCustomPrice: data.isCustomPrice ?? false,
        sellingPriceVat: {
          freeCode: data.vatFreeCode,
          type: data.vatType,
        },
        workItemGroupId: data.workItemGroupId,
        timeOutputPerUnit: data.timeOutputPerUnit,
        isUnitPriceWithVat: itemData?.isUnitPriceWithVat ?? false,
        priceType: itemData?.priceType ?? null,
        isDiscount: !!data.isCustomDiscount,
        isDoNotApplyDiscount: !!data.isDoNotApplyDiscount,
        discount: data.isCustomDiscount
          ? {
              sourceType: data.discountType ?? '',
              source: itemData?.discount?.availableDiscounts?.find(
                (discount) => discount?.sourceType === data.discountType
              )?.source,
              rate:
                (data.discountType === 'manual-discount'
                  ? getDecimalFromPercentage(data.discountRate)
                  : itemData?.discount?.availableDiscounts?.find(
                      (discount) => discount?.sourceType === data.discountType
                    )?.rate) ?? 0,
            }
          : null,
        assignMechanics: data.assignMechanics
          ?.filter((mechanic) => isNotNilOrEmpty(mechanic?.id))
          .map((mechanic, index) => ({
            ...omit(['ratio'], mechanic),
            id: mechanic.id ?? '',
            isDefault: index === 0,
            amount: mechanic?.amount ?? 0,
            ratio: (mechanic?.ratio ?? 0) / 100,
          })),
      },
    })
      .unwrap()
      .then(() => {
        // Saving changes by rowControl change should not close dialog.
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }
        props.onClose();
      })
      .catch(handleApiError);
  };

  const handleQuantityChange =
    (formApi: UseFormReturn<EditItemFormType>) => (value: number | null) => {
      if (isNil(value)) {
        return;
      }

      validateQuantity(value, formApi);

      const sellingPricePerUnit = formApi.getValues('sellingPricePerUnit');

      formApi.setValue(
        'sellingPrice',
        isNotNil(sellingPricePerUnit) ? value * sellingPricePerUnit : 0
      );

      const mechanics = formApi.getValues('assignMechanics');

      if (mechanics?.length === 1) {
        return formApi.setValue(`assignMechanics.0.amount`, value);
      }

      mechanics?.forEach(
        (mechanic, index) =>
          isNotNil(mechanic.amount) &&
          formApi.setValue(
            `assignMechanics.${index}.ratio`,
            Math.round((mechanic.amount / value) * 100)
          )
      );
    };

  const validateQuantity = (value: number, formApi: UseFormReturn<EditItemFormType>) => {
    const valueToValidate = defaultTo(0, value);

    formApi.clearErrors('quantity');

    const validationResult = quantitySchema.safeParse(valueToValidate);

    if (validationResult.success) {
      return;
    }

    const errorMessages = validationResult.error?.errors.map((error) => error.message).join(' ');
    formApi.setError('quantity', {message: errorMessages});
  };

  const defaultValues: EditItemFormType = {
    labourRateId: itemData?.labourRateId ?? '',
    name: itemData?.name,
    number: itemData?.number,
    workType: itemData?.workType,
    quantity:
      itemData?.priceType === 'SVCPRICETYPE_DIRECT_PRICE' ? 1 : (itemData?.quantity?.amount ?? 0),
    originalSellingPricePerUnit: itemData?.sellingPricePerUnit?.amount,
    sellingPricePerUnit: itemData?.sellingPricePerUnit?.amount ?? 0,
    sellingPrice:
      isNotNil(itemData?.sellingPricePerUnit?.amount) && isNotNil(itemData?.quantity?.amount)
        ? itemData?.sellingPricePerUnit?.amount * itemData?.quantity?.amount
        : null,
    originalPurchasePricePerUnit: itemData?.basePurchasePricePerUnit?.amount,
    purchasePricePerUnit: itemData?.purchasePricePerUnit?.amount,
    vatType: itemData?.sellingPriceVat?.type ?? 'S',
    discountRate: getPercentageFromDecimal(
      itemData?.discount?.availableDiscounts?.find(
        (discount) => discount?.sourceType === 'manual-discount'
      )?.rate
    ),
    discountType:
      itemData?.discount?.availableDiscounts?.find((discount) => discount?.isSelected)
        ?.sourceType ?? 'manual-discount',
    isCustomDiscount:
      itemData?.discount?.availableDiscounts?.some((discount) => discount?.isSelected) ?? false,
    isCustomPrice: Boolean(itemData?.isCustomPrice),
    isUnitPriceWithVat: itemData?.isUnitPriceWithVat ?? false,
    vatFreeCode: itemData?.sellingPriceVat?.freeCode ?? undefined,
    assignMechanics: itemData?.assignMechanics?.filter(isNotNil)?.map((mechanic) => ({
      id: mechanic.id ?? null,
      amount:
        itemData?.assignMechanics?.length === 1
          ? (itemData?.quantity?.amount ?? 1)
          : (mechanic?.amount ?? 0),
      ratio:
        itemData?.assignMechanics?.length === 1
          ? TOTAL_RATIO
          : (getRatioFromAmount(mechanic?.amount, itemData.quantity?.amount) ?? 0),
    })) ?? [{id: null, amount: itemData?.quantity?.amount ?? 1, ratio: TOTAL_RATIO}],
    isDoNotApplyDiscount: itemData?.isDoNotApplyDiscount ?? false,
    customPriceType: 'perUnit',
    workItemGroupId: itemData?.workItemGroupId ?? undefined,
    timeOutputPerUnit: itemData?.timeOutputPerUnit ?? undefined,
  };

  const isTimeNormType = itemData?.priceType === 'SVCPRICETYPE_TIME_NORM';
  const isCooperationType = itemData?.priceType === 'SVCPRICETYPE_COOPERATION';
  const isDirectPriceType = itemData?.priceType === 'SVCPRICETYPE_DIRECT_PRICE';
  const isCalculationPriceType = itemData?.priceType === 'SVCPRICETYPE_CALCULATION_PRICE';
  const isSubcontractPriceType = itemData?.priceType === 'SVCPRICETYPE_SUBCONTRACT';

  const goodwillRatio = itemData?.goodwill?.goodwillRatio ?? 1;
  const hasGoodwill = goodwillRatio !== 1;

  const workTypeOptions = getOptionsWithSelectedValue(itemData?.workType);

  const handleCustomPriceSwitch =
    (formApi: UseFormReturn<EditItemFormType>) => (value: boolean) => {
      if (value) {
        formApi.setValue('customPriceType', 'perUnit');
      } else {
        const quantity = formApi.watch('quantity');
        formApi.setValue(
          'sellingPrice',
          isNotNil(quantity) && isNotNil(itemData?.baseSellingPricePerUnit?.amount)
            ? quantity * itemData.baseSellingPricePerUnit.amount
            : 0
        );
        formApi.setValue('sellingPricePerUnit', itemData?.baseSellingPricePerUnit?.amount ?? 0);
        formApi.setValue('purchasePricePerUnit', itemData?.basePurchasePricePerUnit?.amount);
        formApi.setValue('vatType', itemData?.baseSellingPriceVat?.type ?? '');
      }
    };

  const onCustomPricePerUnitChange =
    (formApi: UseFormReturn<EditItemFormType>) => (value: number | Nullish) => {
      const quantity = formApi.watch('quantity');

      formApi.setValue(
        'sellingPrice',
        isNotNil(quantity) && isNotNil(value) ? value * quantity : 0
      );
    };

  const onCustomPriceChange =
    (formApi: UseFormReturn<EditItemFormType>) => (value: number | Nullish) => {
      const quantity = formApi.watch('quantity');

      formApi.setValue(
        'sellingPricePerUnit',
        isNotNil(quantity) && isNotNil(value) ? value / quantity : 0
      );
    };

  const isLoading =
    isItemLoading || isLabourRatesLoading || isItemFetching || isWorkItemGroupLoading;
  const isError = isItemError || isLabourRatesError || isWorkItemGroupError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={143}>
      <Form<EditItemFormType>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        experimentalZodSchema={editItemFormSchema}
        isDisabled={isOrderReadOnly}
      >
        {(control, formApi) => {
          // Used for saving changes by rowControl in parent.
          if (props.formRef) {
            props.formRef.current = {
              submit: () => control.submitForm(),
              isDirty: formApi.formState.isDirty,
            };
          }

          const isTouched = formApi.formState.isDirty;

          return (
            <VStack spacing={4}>
              <Show when={hasGoodwill}>
                <Alert
                  variant="info"
                  type="inline"
                  title={i18n.t('entity.addWork.lables.itemHasGoodwill')}
                  message={i18n.t('entity.addWork.lables.goodwillRatio', {
                    ratio: goodwillRatio * 100,
                  })}
                  data-testid={suffixTestId('itemHasGoodwill', props)}
                />
              </Show>
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    name="number"
                    control={control}
                    type="text"
                    label={i18n.t('general.labels.number')}
                    isDisabled={hasGoodwill}
                    data-testid={suffixTestId('number', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    name="name"
                    control={control}
                    type="text"
                    label={i18n.t('general.labels.name')}
                    isDisabled={hasGoodwill}
                    data-testid={suffixTestId('name', props)}
                  />
                </Box>
              </HStack>
              <Grid columns={4}>
                <FormField
                  name="workType"
                  control={control}
                  type="choice"
                  options={workTypeOptions}
                  label={i18n.t('entity.addWork.lables.workCategory')}
                  placeholder={i18n.t('general.labels.select')}
                  isDisabled={hasGoodwill}
                  menuInPortal
                  data-testid={suffixTestId('workType', props)}
                />
                <FormField
                  name="labourRateId"
                  control={control}
                  type="choice"
                  options={labourRatesOptions ?? []}
                  label={i18n.t('entity.addWork.lables.defaultHourlyRate')}
                  isDisabled={hasGoodwill}
                  menuInPortal
                  data-testid={suffixTestId('labourRateId', props)}
                />
                <Show when={isCalculationPriceType || isDirectPriceType}>
                  <FormField
                    name="timeOutputPerUnit"
                    control={control}
                    type="number"
                    label={i18n.t('entity.addWork.lables.timeOutputPerUnit')}
                    suffix={i18n.t('general.labels.hourCompact')}
                    data-testid={suffixTestId('timeOutputPerUnit', props)}
                  />
                </Show>
                <GridItem span={isCalculationPriceType || isDirectPriceType ? 1 : 2}>
                  <FormField
                    name="workItemGroupId"
                    control={control}
                    type="choice"
                    options={workItemGroupOptions}
                    label={i18n.t('entity.warehouse.labels.workItemGroup')}
                    menuInPortal
                    data-testid={suffixTestId('workItemGroupId', props)}
                  />
                </GridItem>
              </Grid>
              <Card variant="inlineGrey">
                <EditItemMechanics
                  control={control}
                  formApi={formApi}
                  serviceCaseId={props.serviceCaseId}
                  data-testid={suffixTestId('assignMechanics', props)}
                  isDisabled={isOrderReadOnly}
                />
                <Separator />
                <VStack spacing={4}>
                  <HStack spacing={4}>
                    <Hide when={isCooperationType || isSubcontractPriceType}>
                      <Box flex={1}>
                        <FormField
                          name="quantity"
                          control={control}
                          type="number"
                          label={i18n.t('entity.order.itemQuantity')}
                          isDisabled={isDirectPriceType || hasGoodwill}
                          onChange={handleQuantityChange(formApi)}
                          suffix={itemData?.quantity?.unit}
                          data-testid={suffixTestId('quantity', props)}
                        />
                      </Box>
                    </Hide>
                    <Show when={isTimeNormType}>
                      <Box flex={1}>
                        <FormField
                          name="originalSellingPricePerUnit"
                          type="number"
                          isDisabled={isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed)}
                          control={control}
                          label={i18n.t('entity.order.itemPurchasePrice')}
                          suffix={tenant.tenantCurrency}
                          data-testid={suffixTestId('originalSellingPricePerUnit', props)}
                        />
                      </Box>
                    </Show>
                    <Hide when={isTimeNormType}>
                      <Box flex={1}>
                        <FormField
                          name="originalPurchasePricePerUnit"
                          type="number"
                          control={control}
                          label={
                            isCooperationType || isSubcontractPriceType
                              ? i18n.t('general.labels.purchasePrice')
                              : i18n.t('entity.addWork.lables.purchasePricePerUnit')
                          }
                          isDisabled={isFalse(itemData?.purchasePricePerUnit?.isEditingAllowed)}
                          suffix={tenant.tenantCurrency}
                          data-testid={suffixTestId('originalPurchasePricePerUnit', props)}
                        />
                      </Box>
                      <Box flex={1}>
                        <FormField
                          name="originalSellingPricePerUnit"
                          type="number"
                          control={control}
                          label={
                            isCooperationType || isSubcontractPriceType
                              ? i18n.t('general.labels.sellingPrice')
                              : i18n.t('entity.addWork.lables.sellingPricePerUnit')
                          }
                          isDisabled={isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed)}
                          suffix={tenant.tenantCurrency}
                          data-testid={suffixTestId('originalSellingPricePerUnit', props)}
                        />
                      </Box>
                    </Hide>
                    <Box flex={1}>
                      <FormField
                        isNotClearable
                        control={control}
                        type="choice"
                        name="vatType"
                        options={vatOptions}
                        label={i18n.t('entity.addWork.lables.vat')}
                        placeholder={i18n.t('general.labels.select')}
                        isDisabled={
                          hasGoodwill ||
                          not(formApi.watch('isCustomPrice')) ||
                          isFalse(itemData?.sellingPriceVat?.isEditingAllowed)
                        }
                        menuInPortal
                        data-testid={suffixTestId('vatType', props)}
                      />
                    </Box>
                    <Show when={isTimeNormType || isCooperationType || isSubcontractPriceType}>
                      <Box flex={1} />
                    </Show>
                  </HStack>
                  <Box maxWidth="fit-content">
                    <FormField
                      name="isCustomPrice"
                      type="switch"
                      control={control}
                      label={i18n.t('entity.orderItem.labels.customPrice')}
                      onChange={handleCustomPriceSwitch(formApi)}
                      isDisabled={
                        isFalse(itemData?.isCustomPriceEditingAllowed) || !canEditCustomPrice
                      }
                      data-testid={suffixTestId('isCustomPrice', props)}
                    />
                  </Box>
                  <Show when={formApi.watch('isCustomPrice') && isTimeNormType}>
                    <Grid columns={4}>
                      <RadioItem
                        value={formApi.watch('customPriceType') === 'perUnit'}
                        isDisabled={!canEditCustomPrice || isOrderReadOnly}
                        onChange={() => formApi.setValue('customPriceType', 'perUnit')}
                      >
                        {i18n.t('entity.warehouse.labels.pricePerUnit')}
                      </RadioItem>
                      <FormField
                        control={control}
                        type="number"
                        name="sellingPricePerUnit"
                        isDisabled={
                          formApi.watch('customPriceType') !== 'perUnit' ||
                          isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed) ||
                          !canEditCustomPrice
                        }
                        onChange={
                          formApi.watch('customPriceType') === 'perUnit'
                            ? onCustomPricePerUnitChange(formApi)
                            : undefined
                        }
                        suffix={tenant.tenantCurrency}
                        data-testid={suffixTestId('sellingPricePerUnit', props)}
                      />
                      <RadioItem
                        value={formApi.watch('customPriceType') === 'sellingPrice'}
                        isDisabled={!canEditCustomPrice || isOrderReadOnly}
                        onChange={() => formApi.setValue('customPriceType', 'sellingPrice')}
                      >
                        {i18n.t('general.labels.sellingPrice')}
                      </RadioItem>
                      <FormField
                        control={control}
                        type="number"
                        name="sellingPrice"
                        isDisabled={
                          formApi.watch('customPriceType') !== 'sellingPrice' ||
                          isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed) ||
                          !canEditCustomPrice
                        }
                        onChange={
                          formApi.watch('customPriceType') === 'sellingPrice'
                            ? onCustomPriceChange(formApi)
                            : undefined
                        }
                        suffix={tenant.tenantCurrency}
                        data-testid={suffixTestId('sellingPrice', props)}
                      />
                    </Grid>
                  </Show>
                  <Show when={formApi.watch('isCustomPrice') && !isTimeNormType}>
                    <Grid columns={4}>
                      <FormField
                        control={control}
                        label={i18n.t(
                          isCalculationPriceType
                            ? 'entity.addWork.lables.purchasePricePerUnit'
                            : 'general.labels.purchasePrice'
                        )}
                        type="number"
                        name="purchasePricePerUnit"
                        isDisabled={
                          isFalse(itemData?.purchasePricePerUnit?.isEditingAllowed) ||
                          !canEditCustomPrice
                        }
                        suffix={tenant.tenantCurrency}
                        data-testid={suffixTestId('sellingPricePerUnit', props)}
                      />
                      <FormField
                        control={control}
                        label={i18n.t(
                          isCalculationPriceType
                            ? 'entity.addWork.lables.sellingPricePerUnit'
                            : 'general.labels.sellingPrice'
                        )}
                        type="number"
                        name="sellingPricePerUnit"
                        isDisabled={
                          isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed) ||
                          !canEditCustomPrice
                        }
                        suffix={tenant.tenantCurrency}
                        data-testid={suffixTestId('sellingPricePerUnit', props)}
                      />
                    </Grid>
                  </Show>
                  <HStack justify="space-between" align="center">
                    <FormField
                      control={control}
                      type="switch"
                      name="isCustomDiscount"
                      label={i18n.t('general.labels.discount')}
                      isDisabled={
                        isFalsy(itemData?.discount?.isEditingAllowed) || !canEditManualDiscount
                      }
                      data-testid={suffixTestId('discount', props)}
                    />
                    <FormField
                      control={control}
                      name="isDoNotApplyDiscount"
                      type="checkbox"
                      isDisabled={isFalsy(itemData?.isDoNotApplyDiscountEditable)}
                      label={i18n.t('general.labels.doNotApplyDiscount')}
                      data-testid={suffixTestId('doNotApplyDiscount', props)}
                    />
                  </HStack>
                  <Show
                    when={
                      formApi.watch('isCustomDiscount') &&
                      not(itemData?.discount?.isDoNotApplyDiscount)
                    }
                  >
                    <Grid columns={4}>
                      <Show
                        when={itemData?.discount?.availableDiscounts?.find(
                          (discount) => discount?.sourceType === 'customer-contract'
                        )}
                      >
                        <RadioItem
                          value={formApi.watch('discountType') === 'customer-contract'}
                          isDisabled={!canEditManualDiscount || isOrderReadOnly}
                          onChange={() =>
                            formApi.setValue('discountType', 'customer-contract', {
                              shouldDirty: true,
                            })
                          }
                        >
                          {i18n.t('entity.customerContract.labels.customerContract')}
                        </RadioItem>
                        <NumberInput
                          isDisabled
                          value={
                            getPercentageFromDecimal(
                              itemData?.discount?.availableDiscounts?.find(
                                (discount) => discount?.sourceType === 'customer-contract'
                              )?.rate
                            ) ?? 0
                          }
                          suffix="%"
                          data-testid={suffixTestId('customerContract', props)}
                        />
                      </Show>
                      <Show
                        when={itemData?.discount?.availableDiscounts?.find(
                          (discount) => discount?.sourceType === 'custom-discounts'
                        )}
                      >
                        <RadioItem
                          value={formApi.watch('discountType') === 'custom-discounts'}
                          isDisabled={!canEditManualDiscount || isOrderReadOnly}
                          onChange={() =>
                            formApi.setValue('discountType', 'custom-discounts', {
                              shouldDirty: true,
                            })
                          }
                        >
                          {i18n.t('entity.orderItem.labels.customDiscount')}
                        </RadioItem>
                        <NumberInput
                          isDisabled
                          value={
                            getPercentageFromDecimal(
                              itemData?.discount?.availableDiscounts?.find(
                                (discount) => discount?.sourceType === 'custom-discounts'
                              )?.rate ?? 0
                            ) ?? 0
                          }
                          suffix="%"
                          data-testid={suffixTestId('customDiscount', props)}
                        />
                      </Show>
                      <Show
                        when={itemData?.discount?.availableDiscounts?.find(
                          (discount) => discount?.sourceType === 'manual-discount'
                        )}
                      >
                        <RadioItem
                          value={formApi.watch('discountType') === 'manual-discount'}
                          isDisabled={!canEditManualDiscount || isOrderReadOnly}
                          onChange={() =>
                            formApi.setValue('discountType', 'manual-discount', {
                              shouldDirty: true,
                            })
                          }
                        >
                          {i18n.t('entity.orderItem.labels.manualDiscount')}
                        </RadioItem>
                        <FormField
                          control={control}
                          type="number"
                          name="discountRate"
                          decimalPlaces={2}
                          suffix="%"
                          maxStepperValue={100}
                          minStepperValue={0}
                          isDisabled={
                            formApi.watch('discountType') !== 'manual-discount' ||
                            !canEditManualDiscount
                          }
                          data-testid={suffixTestId('discount', props)}
                        />
                      </Show>
                    </Grid>
                  </Show>
                </VStack>
                <Separator />
                <ItemPrice
                  itemId={props.itemId}
                  serviceCaseId={props.serviceCaseId}
                  serviceOrderId={props.serviceOrderId}
                  serviceJobId={props.serviceJobId}
                  amount={formApi.watch('quantity')}
                  purchasePricePerUnit={formApi.watch('purchasePricePerUnit')}
                  purchaseCurrency={itemData?.purchasePricePerUnit?.currency}
                  sellingPricePerUnit={formApi.watch('sellingPricePerUnit')}
                  sellingCurrency={itemData?.sellingPricePerUnit?.currency}
                  vatType={formApi.watch('vatType')}
                  priceType={itemData?.priceType ?? ''}
                  workType={formApi.watch('workType')}
                  assignMechanics={formApi.watch('assignMechanics')}
                  discountRate={match([
                    formApi.watch('isCustomDiscount'),
                    formApi.watch('discountType'),
                  ])
                    .with([false, Pattern.any], () => 0)
                    .with([true, 'manual-discount'], () =>
                      getDecimalFromPercentage(formApi.watch('discountRate'))
                    )
                    .with([true, Pattern.any], () => {
                      const discountRate = itemData?.discount?.availableDiscounts?.find(
                        (discount) => discount?.sourceType === formApi.watch('discountType')
                      )?.rate;

                      return isNil(discountRate) ? 0 : discountRate;
                    })
                    .otherwise(() => 0)}
                  showMargin={canReadServiceOrderFinancesLabourItem}
                  showPurchasePrice={canReadServiceOrderFinancesLabourItem}
                  isDisabled={isOrderReadOnly}
                />
              </Card>
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    onClick={props.onClose}
                    variant="secondary"
                    title={i18n.t('general.actions.discard')}
                    data-testid={suffixTestId('discard', props)}
                  />
                  <FormButton
                    isDisabled={!isTouched}
                    control={control}
                    type="submit"
                    title={i18n.t('general.actions.saveChanges')}
                    isLoading={isPatchLoading}
                    data-testid={suffixTestId('save', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}

const quantitySchema = z.number().positive();
